// src/components/Admin/Feedback.jsx
import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Star,
  User,
  Calendar,
  ThumbsUp,
  ThumbsDown,
  Search,
  TrendingUp,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Reply,
  Archive
} from "lucide-react";

import API_BASE_URL from "../../services/config";

import { authFetch } from "../../auth";

const Feedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [filteredFeedback, setFilteredFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const ratings = [
    { value: "all", label: "All Ratings" },
    { value: "5", label: "★★★★★" },
    { value: "4", label: "★★★★" },
    { value: "3", label: "★★★" },
    { value: "2", label: "★★" },
    { value: "1", label: "★" }
  ];

  const statuses = [
    { value: "all", label: "All Status" },
    { value: "new", label: "New", color: "blue" },
    { value: "read", label: "Read", color: "gray" },
    { value: "replied", label: "Replied", color: "green" },
    { value: "archived", label: "Archived", color: "yellow" }
  ];

  useEffect(() => {
    fetchFeedback();
  }, []);

  useEffect(() => {
    filterFeedback();
  }, [feedback, searchTerm, ratingFilter, statusFilter, dateFilter]);

  const fetchFeedback = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await authFetch(
          `${API_BASE_URL}/api/admin/feedback`,
        { method: "GET" }
      );

      if (!res.ok) {
        throw new Error(`Failed to load feedback (${res.status})`);
      }

      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.content)
        ? data.content
        : [];

      setFeedback(list);
    } catch (err) {
      console.error("Error fetching feedback:", err);
      setError(err.message || "Failed to load feedback");
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  const filterFeedback = () => {
    let filtered = [...feedback];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((fb) => {
        const comment = (fb.message || fb.comment || "").toLowerCase();
        const userName = (fb.name || fb.user?.name || "").toLowerCase();
        const userEmail = (fb.email || fb.user?.email || "").toLowerCase();
        const feedbackType = (fb.type || "").toLowerCase();
        const questionTitle = (fb.questionTitle || "").toLowerCase();
        return (
          comment.includes(term) ||
          userName.includes(term) ||
          userEmail.includes(term) ||
          feedbackType.includes(term) ||
          questionTitle.includes(term)
        );
      });
    }

    if (ratingFilter !== "all") {
      const ratingNum = parseInt(ratingFilter, 10);
      filtered = filtered.filter(
        (fb) => (fb.rating || 0) === ratingNum
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((fb) => fb.status === statusFilter);
    }

    if (dateFilter !== "all") {
      const now = new Date();
      const cutoff = new Date();

      switch (dateFilter) {
        case "today":
          cutoff.setHours(0, 0, 0, 0);
          break;
        case "week":
          cutoff.setDate(now.getDate() - 7);
          break;
        case "month":
          cutoff.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }

      filtered = filtered.filter((fb) => {
        if (!fb.createdAt) return false;
        const created = new Date(fb.createdAt);
        return created >= cutoff;
      });
    }

    setFilteredFeedback(filtered);
  };

  const getRatingStars = (rating) => {
    const r = rating || 0;
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < r ? "text-yellow-500 fill-yellow-500" : "text-gray-300"
          }`}
        />
      ));
  };

  const getStatusBadge = (status) => {
    const configs = {
      new: {
        color: "bg-blue-100 text-blue-800",
        icon: <Clock className="h-3 w-3" />
      },
      read: {
        color: "bg-gray-100 text-gray-800",
        icon: <CheckCircle className="h-3 w-3" />
      },
      replied: {
        color: "bg-green-100 text-green-800",
        icon: <Reply className="h-3 w-3" />
      },
      archived: {
        color: "bg-yellow-100 text-yellow-800",
        icon: <Archive className="h-3 w-3" />
      }
    };

    const config =
      configs[status] || {
        color: "bg-gray-100 text-gray-800",
        icon: <AlertCircle className="h-3 w-3" />
      };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.icon}
        <span className="ml-1">
          {status
            ? status.charAt(0).toUpperCase() + status.slice(1)
            : "Unknown"}
        </span>
      </span>
    );
  };

  const getSentimentIcon = (rating) => {
    if (rating >= 4) {
      return <ThumbsUp className="h-5 w-5 text-green-500" />;
    }
    if (rating >= 3) {
      return <MessageSquare className="h-5 w-5 text-blue-500" />;
    }
    return <ThumbsDown className="h-5 w-5 text-red-500" />;
  };

  const handleReply = async (feedbackId) => {
    if (!replyText.trim()) return;

    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/admin/feedback/${feedbackId}/reply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ replyText })
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to send reply (${res.status})`);
      }

      setReplyTo(null);
      setReplyText("");
      fetchFeedback();
    } catch (err) {
      console.error("Error sending reply:", err);
      setError(err.message || "Failed to send reply");
    }
  };

  const markAsRead = async (feedbackId) => {
    try {
      const res = await authFetch(
         `${API_BASE_URL}/api/admin/feedback/${feedbackId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ status: "read" })
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to update feedback status (${res.status})`);
      }

      fetchFeedback();
    } catch (err) {
      console.error("Error marking as read:", err);
      setError(err.message || "Failed to update feedback status");
    }
  };

  const exportFeedback = () => {
    if (filteredFeedback.length === 0) return;

    const esc = (v) => `"${String(v || "").replace(/"/g, '""')}"`;

    const headers = [
      "Date",
      "User",
      "Email",
      "Rating",
      "Comment",
      "Status",
      "Question",
      "Response"
    ];

    const rows = filteredFeedback.map((fb) => [
      esc(
        fb.createdAt
          ? new Date(fb.createdAt).toLocaleDateString()
          : "Unknown"
      ),
      esc(fb.name || fb.user?.name || "Anonymous"),
      esc(fb.email || fb.user?.email || "No email"),
      esc(fb.rating ?? "N/A"),
      esc(fb.message || fb.comment || ""),
      esc(fb.status || "N/A"),
      esc(fb.questionTitle || "N/A"),
      esc(fb.adminReply || "No reply")
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join(
      "\n"
    );

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      "feedback-" + new Date().toISOString().split("T")[0] + ".csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStats = () => {
    const total = feedback.length;
    const ratingsOnly = feedback
      .map((fb) => fb.rating)
      .filter((r) => typeof r === "number");

    const avgRating =
      ratingsOnly.length > 0
        ? (
            ratingsOnly.reduce((acc, r) => acc + r, 0) / ratingsOnly.length
          ).toFixed(1)
        : 0;

    const newCount = feedback.filter((fb) => fb.status === "new").length;
    const repliedCount = feedback.filter(
      (fb) => fb.status === "replied"
    ).length;

    return { total, avgRating, newCount, repliedCount };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-pink-500 border-t-transparent"></div>
        <p className="text-pink-600 font-medium">Loading feedback...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <MessageSquare className="h-8 w-8" />
              <h1 className="text-2xl font-bold">User Feedback</h1>
            </div>
            <p className="text-pink-100">
              User reviews and ratings for answers and overall experience
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{stats.total}</div>
            <p className="text-pink-100 text-sm">Total Feedback</p>
          </div>
        </div>
        {error && (
          <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Star className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Average Rating</p>
              <p className="text-xl font-bold">{stats.avgRating} / 5</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <MessageSquare className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">New Feedback</p>
              <p className="text-xl font-bold">{stats.newCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <Reply className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Replied</p>
              <p className="text-xl font-bold">{stats.repliedCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Positive (4-5★)</p>
              <p className="text-xl font-bold">
                {feedback.filter((fb) => (fb.rating || 0) >= 4).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search feedback..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
          </div>

          {/* Rating Filter */}
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            {ratings.map((rating) => (
              <option key={rating.value} value={rating.value}>
                {rating.label}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            {statuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>

          {/* Export Button */}
          <button
            onClick={exportFeedback}
            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg hover:opacity-90"
          >
            <Download className="h-5 w-5" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        {filteredFeedback.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No feedback found
            </h3>
            <p className="text-gray-600">Try adjusting your filters</p>
          </div>
        ) : (
          filteredFeedback.map((fb) => {
            const rating = fb.rating || 0;
            const userName = fb.name || fb.user?.name || "Anonymous";
            const userEmail = fb.email || fb.user?.email || "No email";
            const initial = (
              userName || userEmail || "U"
            )
              .charAt(0)
              .toUpperCase();
            const canManageFeedback = typeof fb.status === "string";

            return (
              <div
                key={fb.id}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          {getRatingStars(rating)}
                        </div>
                        {getStatusBadge(fb.status)}
                        <div className="flex items-center text-gray-500 text-sm">
                          {getSentimentIcon(rating)}
                          <span className="ml-1">
                            {rating >= 4
                              ? "Positive"
                              : rating >= 3
                              ? "Neutral"
                              : "Negative"}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          <Calendar className="inline h-3 w-3 mr-1" />
                          {fb.createdAt
                            ? new Date(
                                fb.createdAt
                              ).toLocaleDateString()
                            : "Unknown"}
                        </span>
                        {fb.status === "new" && (
                          <button
                            onClick={() => markAsRead(fb.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white font-bold">
                        {initial}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {userName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {userEmail}
                        </div>
                      </div>
                    </div>

                    {fb.type && (
                      <div className="mb-4">
                        <span className="inline-flex rounded-full bg-pink-50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-pink-700 border border-pink-200">
                          {fb.type}
                        </span>
                      </div>
                    )}

                    {/* Feedback Content */}
                    <div className="mb-4">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {fb.message || fb.comment || "(No feedback message provided)"}
                      </p>
                    </div>

                    {/* Question Reference */}
                    {fb.questionTitle && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-blue-700 font-medium mb-1">
                              Feedback for:
                            </div>
                            <div className="text-gray-900 font-medium">
                              {fb.questionTitle}
                            </div>
                          </div>
                          <button className="text-blue-600 hover:text-blue-800">
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Admin Reply */}
                    {fb.adminReply && (
                      <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-100">
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <Reply className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm text-green-700 font-medium mb-1">
                              Your Reply
                            </div>
                            <p className="text-gray-700">
                              {fb.adminReply}
                            </p>
                            {fb.repliedAt && (
                              <div className="text-xs text-gray-500 mt-2">
                                Replied on{" "}
                                {new Date(
                                  fb.repliedAt
                                ).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reply Form */}
                    {canManageFeedback && replyTo === fb.id && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <textarea
                          value={replyText}
                          onChange={(e) =>
                            setReplyText(e.target.value)
                          }
                          placeholder="Type your reply here..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 mb-3"
                          rows="3"
                        />
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => {
                              setReplyTo(null);
                              setReplyText("");
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleReply(fb.id)}
                            className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg hover:opacity-90"
                          >
                            Send Reply
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {canManageFeedback && !fb.adminReply && replyTo !== fb.id && (
                      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => setReplyTo(fb.id)}
                          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg hover:opacity-90"
                        >
                          <Reply className="h-4 w-4" />
                          <span>Reply to Feedback</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Summary Footer */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">
              Feedback Summary
            </h3>
            <p className="text-sm text-gray-600">
              {filteredFeedback.length} feedback items •{" "}
              {stats.avgRating} average rating • {stats.newCount} new
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              <Star className="inline h-4 w-4 mr-1 text-yellow-500" />
              Rating Distribution:
            </div>
            <div className="flex items-center space-x-2">
              {[5, 4, 3, 2, 1].map((rating) => (
                <div
                  key={rating}
                  className="flex items-center space-x-1"
                >
                  <span className="text-xs text-gray-500">
                    {rating}★
                  </span>
                  <span className="text-sm font-medium">
                    {
                      feedback.filter(
                        (fb) => (fb.rating || 0) === rating
                      ).length
                    }
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
